import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { startOfToday, endOfToday, startOfYear, endOfYear } from "date-fns";

import { Box, Typography, Stack } from "@mui/material";

import useProject from "../../hooks/useProject";

import Loading from "./Loading";
import Project from "./Project";
import EditorToolbar from "../editors/EditorToolbar";

import ProjectModal from "../modals/ProjectModal";

const Projects = ({ clientId }) => {
  const navigate = useNavigate();

  const { getProjects, projects } = useProject();

  const [openEditor, setOpenEditor] = useState(false);
  const [filterValue, setFilterValue] = useState("activeOnly");

  useEffect(() => {
    getData();
  }, [clientId, filterValue]);

  const getData = async () => {
    let filter = { clientId };

    switch (filterValue) {
      case "activeOnly":
        filter.isActive = true;
        break;

      case "current":
        filter.startsOn = { $lte: startOfToday() };
        filter.endsOn = { $gte: endOfToday() };
        break;

      case "thisYear":
        filter.startsOn = { $gte: startOfYear(new Date()) };
        filter.endsOn = { $lte: endOfYear(new Date()) };
        break;
    }

    getProjects(filter);
  };

  const handleOpenProjectEditor = () => {
    setOpenEditor(true);
  };

  const handleSelectOption = (value) => {
    setFilterValue(value);
  };

  const handleOpenProject = (projectId) => {
    navigate("/project", { state: { projectId, clientId } });
  };

  const handleSavedProject = async () => {
    setOpenEditor(false);
    await getData();
  };

  const sortedProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];

    const getStartKey = (project) => {
      if (project?.startsOn) {
        const value = new Date(project.startsOn);
        if (!Number.isNaN(value.getTime())) return value.getTime();
      }

      if (project?.yyyymmddStart) {
        const value = new Date(project.yyyymmddStart);
        if (!Number.isNaN(value.getTime())) return value.getTime();
      }

      return Number.MIN_SAFE_INTEGER;
    };

    return [...projects].sort((left, right) => {
      const leftStart = getStartKey(left);
      const rightStart = getStartKey(right);

      if (leftStart !== rightStart) {
        return rightStart - leftStart;
      }

      return (left?.name || "").localeCompare(right?.name || "");
    });
  }, [projects]);

  if (!projects) return <Loading />;

  return (
    <>
      <Box>
        <EditorToolbar
          onAdd={() => handleOpenProjectEditor(null)}
          selectOptions={{
            label: "",
            options: [
              { id: "all", name: "All" },
              { id: "activeOnly", name: "Active Only" },
              { id: "current", name: "Current" },
              { id: "thisYear", name: "This Year" },
            ],
          }}
          onSelect={handleSelectOption}
          selectValue={filterValue}
        />
        <Stack
          spacing={1}
          sx={{ padding: "8px" }}
        >
          {sortedProjects.map((project) => (
            <Project
              project={project}
              onClick={handleOpenProject}
            />
          ))}
        </Stack>
      </Box>

      {openEditor && (
        <ProjectModal
          clientId={clientId}
          projectId={null}
          open={openEditor}
          onClose={() => setOpenEditor(false)}
          onSave={handleSavedProject}
        />
      )}
    </>
  );
};

export default Projects;
